// frontend/src/components/Common/SearchableSelect.jsx

import React, { useState, useEffect, useRef } from 'react';
import { FiSearch, FiX, FiChevronDown } from 'react-icons/fi';
import './SearchableSelect.css';

// ── Multi-select variant (separate component to obey Rules of Hooks) ──────────
const SearchableSelectMulti = ({
  value = [],
  onChange,
  options = [],
  getOptionLabel,
  getOptionValue,
  placeholder = 'Select...',
  disabled = false,
  className = '',
}) => {
  const selected = Array.isArray(value) ? value : (value ? [String(value)] : []);
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref                  = useRef(null);
  const searchRef            = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const toggle = v => {
    const s = String(v);
    onChange(selected.includes(s) ? selected.filter(x => x !== s) : [...selected, s]);
  };

  const filtered = options.filter(o =>
    !search || getOptionLabel(o).toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabels = selected.map(v => {
    const o = options.find(o => String(getOptionValue(o)) === v);
    return o ? getOptionLabel(o) : v;
  });

  return (
    <div className={`ss-wrap ${className} ${disabled ? 'ss-disabled' : ''}`} ref={ref}>
      <div
        className={`ss-trigger ${open ? 'ss-open' : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') !disabled && setOpen(o => !o); }}
      >
        <span className="ss-display" style={{ flex: 1, minWidth: 0 }}>
          {selected.length === 0
            ? <span className="ss-placeholder">{placeholder}</span>
            : <span className="ss-multi-pills">
                {selectedLabels.map((lbl, i) => (
                  <span key={i} className="ss-multi-pill">
                    {lbl}
                    <span className="ss-multi-pill-x"
                      onClick={e => { e.stopPropagation(); toggle(selected[i]); }}>
                      <FiX size={10} />
                    </span>
                  </span>
                ))}
              </span>
          }
        </span>
        <span className="ss-controls">
          {selected.length > 0 && (
            <span className="ss-clear" onClick={e => { e.stopPropagation(); onChange([]); }} title="Limpar">
              <FiX size={13} />
            </span>
          )}
          <span className={`ss-chevron ${open ? 'ss-chevron-open' : ''}`}>
            <FiChevronDown size={14} />
          </span>
        </span>
      </div>

      {open && (
        <div className="ss-dropdown">
          <div className="ss-search-row">
            <FiSearch size={13} className="ss-search-icon" />
            <input ref={searchRef} className="ss-search-input" placeholder="Pesquisar…"
              value={search} onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()} />
            {search && (
              <span className="ss-search-clear" onClick={() => setSearch('')}>
                <FiX size={12} />
              </span>
            )}
          </div>
          {selected.length > 0 && (
            <div className="ss-multi-clear-all" onClick={() => { onChange([]); setOpen(false); }}>
              Limpar seleção ({selected.length})
            </div>
          )}
          <div className="ss-list">
            {filtered.length === 0
              ? <div className="ss-no-results">Sem resultados</div>
              : filtered.map((opt, i) => {
                  const v = String(getOptionValue(opt));
                  const isSel = selected.includes(v);
                  return (
                    <div key={v ?? i}
                      className={`ss-option ${isSel ? 'ss-option-selected' : ''}`}
                      onClick={() => toggle(v)}>
                      <span className="ss-multi-check">{isSel ? '✓' : ''}</span>
                      {getOptionLabel(opt)}
                    </div>
                  );
                })
            }
          </div>
        </div>
      )}
    </div>
  );
};

const SearchableSelect = ({
  value,
  onChange,
  options = [],
  getOptionLabel,
  getOptionValue,
  formatDisplay,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  isClearable = false, // accepted but handled by parent
  multi = false,       // handled below — renders SearchableSelectMulti
}) => {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref                  = useRef(null);
  const searchRef            = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search when dropdown opens
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  // Find selected option
  const selected = value !== '' && value !== null && value !== undefined
    ? options.find(opt => String(getOptionValue(opt)) === String(value))
    : null;

  const displayText = selected
    ? (formatDisplay ? formatDisplay(selected) : getOptionLabel(selected))
    : null;

  // Filter options
  const filtered = options.filter(opt => {
    if (!search) return true;
    const label = getOptionLabel(opt).toLowerCase();
    return label.includes(search.toLowerCase());
  });

  const handleSelect = (opt) => {
    onChange(getOptionValue(opt));
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  const handleToggle = () => {
    if (!disabled) setOpen(v => !v);
  };

  return (
    <div className={`ss-wrap ${className} ${disabled ? 'ss-disabled' : ''}`} ref={ref}>
      {/* Trigger */}
      <div
        className={`ss-trigger ${open ? 'ss-open' : ''} ${!selected ? 'ss-empty' : ''}`}
        onClick={handleToggle}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleToggle(); }}
        title={displayText || ''}
      >
        <span className="ss-display">
          {displayText
            ? <span className="ss-value">{displayText}</span>
            : <span className="ss-placeholder">{placeholder}</span>
          }
        </span>
        <span className="ss-controls">
          {selected && (
            <span className="ss-clear" onClick={handleClear} title="Clear">
              <FiX size={13} />
            </span>
          )}
          <span className={`ss-chevron ${open ? 'ss-chevron-open' : ''}`}>
            <FiChevronDown size={14} />
          </span>
        </span>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="ss-dropdown">
          <div className="ss-search-row">
            <FiSearch size={13} className="ss-search-icon" />
            <input
              ref={searchRef}
              className="ss-search-input"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            {search && (
              <span className="ss-search-clear" onClick={() => setSearch('')}>
                <FiX size={12} />
              </span>
            )}
          </div>
          <div className="ss-list">
            {/* Clear/empty option */}
            <div
              className={`ss-option ss-option-empty ${!selected ? 'ss-option-selected' : ''}`}
              onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
            >
              <span className="ss-opt-placeholder">{placeholder}</span>
            </div>
            {filtered.length === 0 ? (
              <div className="ss-no-results">No results</div>
            ) : (
              filtered.map((opt, i) => {
                const optVal = getOptionValue(opt);
                const isSelected = selected && String(getOptionValue(selected)) === String(optVal);
                return (
                  <div
                    key={optVal ?? i}
                    className={`ss-option ${isSelected ? 'ss-option-selected' : ''}`}
                    onClick={() => handleSelect(opt)}
                  >
                    {getOptionLabel(opt)}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// ── Wrapper: delegates to multi or single based on prop ──────────────────────
const SearchableSelectWrapper = (props) => {
  if (props.multi) return <SearchableSelectMulti {...props} />;
  return <SearchableSelect {...props} />;
};

export default SearchableSelectWrapper;